import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCog, FaTimes, FaInfoCircle, FaExclamationCircle, FaBrain } from 'react-icons/fa';
import { 
  getUserAIPreferences, 
  updateUserAIPreferences, 
  getAvailableAIModels,
  getUserAssignedModels,
  setDefaultAIModel
} from '../services/preferences.service';
import { userService } from '../services/user.service';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
  font-size: 14px;
  margin-bottom: 5px;
  
  &:focus {
    outline: none;
    border-color: #0084ff;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
  font-size: 14px;
  margin-bottom: 5px;
  
  &:focus {
    outline: none;
    border-color: #0084ff;
  }
`;

const InfoText = styled.p`
  font-size: 13px;
  color: #666;
  margin-top: 5px;
  font-style: italic;
`;

const InfoBox = styled.div`
  background-color: #f0f7ff;
  border-left: 4px solid #0084ff;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  
  p {
    margin: 0;
    font-size: 14px;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  color: #666;
  
  &:hover:not(:disabled) {
    background-color: #e5e5e5;
  }
`;

const SaveButton = styled(Button)`
  background-color: #0084ff;
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #0073e6;
  }
`;

const ModelCard = styled.div`
  border: 1px solid ${props => props.selected ? '#0084ff' : '#ddd'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.selected ? '#f0f7ff' : '#fff'};
  box-shadow: ${props => props.selected ? '0 2px 8px rgba(0, 132, 255, 0.15)' : 'none'};
  
  &:hover {
    border-color: #0084ff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const ModelName = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModelDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const ProviderTag = styled.span`
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 12px;
  background-color: ${props => props.provider === 'openai' ? '#f0f7ff' : '#f5f5f5'};
  color: ${props => props.provider === 'openai' ? '#0084ff' : '#666'};
  font-weight: 500;
`;

const RuleCard = styled(ModelCard)`
  border-color: ${props => props.enabled ? '#4caf50' : '#ddd'};
  background-color: ${props => props.enabled ? '#f1f8f1' : '#fff'};
  
  &:hover {
    border-color: ${props => props.enabled ? '#43a047' : '#0084ff'};
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.enabled ? '#4caf50' : '#ccc'};
  transition: .4s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
    transform: ${props => props.enabled ? 'translateX(20px)' : 'translateX(0)'};
  }
`;

const AISettingsModal = ({ isOpen, onClose, onSaved }) => {
  const [preferences, setPreferences] = useState({
    ai_provider: 'openai',
    ai_model: 'gpt-4o',
    openai_api_key: '',
    ollama_api_url: ''
  });
  
  const [hasExistingKeys, setHasExistingKeys] = useState({
    openai_api_key: false,
    ollama_api_url: false
  });
  
  const [availableModels, setAvailableModels] = useState({
    providers: []
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [assignedModels, setAssignedModels] = useState([]);
  const [loadingAssignedModels, setLoadingAssignedModels] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);
  
  useEffect(() => {
    // When preferences are loaded, make sure we have at least one model in the list
    if (!loadingAssignedModels && assignedModels.length === 0 && preferences.ai_provider) {
      // Add the current user preference as a model
      setAssignedModels([{
        ai_provider: preferences.ai_provider,
        ai_model: preferences.ai_model,
        is_default: true,
        created_at: new Date().toISOString()
      }]);
    }
  }, [preferences, loadingAssignedModels, assignedModels.length]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Set default preferences in case API calls fail
      const defaultPreferences = {
        ai_provider: 'openai',
        ai_model: 'gpt-4o',
        has_openai_key: false,
        has_ollama_url: false
      };
      
      // Set default available models in case API calls fail
      const defaultModels = {
        providers: [
          {
            name: 'openai',
            display_name: 'OpenAI',
            models: [
              { id: 'gpt-4o', name: 'GPT-4o' },
              { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' }
            ]
          },
          {
            name: 'ollama',
            display_name: 'Ollama',
            models: [
              { id: 'llama3', name: 'Llama 3' }
            ]
          }
        ]
      };
      
      // Fetch user preferences with error handling
      try {
        const prefsResponse = await getUserAIPreferences();
        console.log('AI preferences response:', prefsResponse);
        
        if (prefsResponse && prefsResponse.success) {
          setPreferences(prefsResponse.preferences);
        } else {
          console.warn('Failed to get AI preferences, using defaults');
          setPreferences(defaultPreferences);
        }
      } catch (prefsError) {
        console.error('Error fetching AI preferences:', prefsError);
        setPreferences(defaultPreferences);
      }
      
      // Fetch available AI models with error handling
      try {
        const modelsResponse = await getAvailableAIModels();
        console.log('Available AI models response:', modelsResponse);
        
        if (modelsResponse && modelsResponse.providers) {
          setAvailableModels(modelsResponse);
        } else {
          console.warn('Failed to get available AI models, using defaults');
          setAvailableModels(defaultModels);
        }
      } catch (modelsError) {
        console.error('Error fetching available AI models:', modelsError);
        setAvailableModels(defaultModels);
      }
      
      // Fetch assigned models for the current user
      await fetchAssignedModels();
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(`Error loading settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAssignedModels = async () => {
    try {
      setLoadingAssignedModels(true);
      
      // For debugging
      console.log('Fetching assigned models for current user');
      
      try {
        // Use the new endpoint that doesn't require a specific user ID
        const response = await getUserAssignedModels();
        console.log('Assigned models response:', response);
        
        if (response && response.success && response.models && response.models.length > 0) {
          // If we have assigned models from the API, use those
          console.log('Found assigned models:', response.models.length);
          setAssignedModels(response.models);
          
          // Check if any model is marked as default
          const hasDefault = response.models.some(model => model.is_default);
          
          // If no default is set, mark the first one as default
          if (!hasDefault && response.models.length > 0) {
            const updatedModels = [...response.models];
            updatedModels[0] = { ...updatedModels[0], is_default: true };
            setAssignedModels(updatedModels);
          }
        } else {
          console.log('No assigned models found, using current model as fallback');
          // Always show at least the current model
          const currentModel = {
            ai_provider: preferences.ai_provider || 'openai',
            ai_model: preferences.ai_model || 'gpt-4o',
            is_default: true,
            created_at: new Date().toISOString()
          };
          setAssignedModels([currentModel]);
        }
      } catch (apiError) {
        console.error('API error fetching assigned models:', apiError);
        // If API fails, still show the current model
        const currentModel = {
          ai_provider: preferences.ai_provider || 'openai',
          ai_model: preferences.ai_model || 'gpt-4o',
          is_default: true,
          created_at: new Date().toISOString()
        };
        setAssignedModels([currentModel]);
      }
    } catch (error) {
      console.error('Error in fetchAssignedModels:', error);
      // Create a fallback model from preferences if there's an error
      const fallbackModel = {
        ai_provider: preferences.ai_provider || 'openai',
        ai_model: preferences.ai_model || 'gpt-4o',
        is_default: true,
        created_at: new Date().toISOString()
      };
      setAssignedModels([fallbackModel]);
    } finally {
      setLoadingAssignedModels(false);
    }
  };
  
  const handleSetDefaultModel = async (provider, model) => {
    try {
      setSaving(true);
      setError(null);
      
      console.log(`Setting default model: ${provider}/${model}`);
      
      // Skip API key check entirely - allow users to select any model
      // The backend will handle the validation if needed
      
      // Use the new setDefaultAIModel function
      const response = await setDefaultAIModel(provider, model);
      console.log('Set default model response:', response);
      
      if (response && response.success) {
        // Update the local state to reflect the new default
        const updatedModels = assignedModels.map(m => ({
          ...m,
          is_default: m.ai_provider === provider && m.ai_model === model
        }));
        
        setAssignedModels(updatedModels);
        
        // Also update the preferences
        setPreferences(prev => ({
          ...prev,
          ai_provider: provider,
          ai_model: model
        }));
        
        // Refresh the assigned models
        await fetchAssignedModels();
        
        // Call onSaved to update parent components
        if (onSaved) {
          onSaved({
            ai_provider: provider,
            ai_model: model
          });
        }
      } else {
        throw new Error(response?.message || 'Failed to set default model');
      }
    } catch (error) {
      console.error('Error setting default model:', error);
      setError(`Failed to set default model: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Helper function to check if user has the required API key for a provider
  const checkRequiredApiKey = (provider) => {
    switch (provider) {
      case 'openai':
        return !!preferences.has_openai_key;
      case 'gemini':
        return !!preferences.has_gemini_key;
      case 'claude':
        return !!preferences.has_claude_key;
      case 'groq':
        return !!preferences.has_groq_key;
      case 'ollama':
        return true;
      default:
        return false;
    }
  };
  
  // Determine if a model should be disabled based on API key availability
  const isModelDisabled = (provider) => {
    if (provider === 'ollama') return false; // Ollama is always available
    
    // For other providers, check if the API key is available
    return !checkRequiredApiKey(provider);
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay style={{ display: isOpen ? 'flex' : 'none' }}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            <FaCog style={{ marginRight: '8px' }} />
            AI Settings
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading settings...</div>
          ) : (
            <div>
              <InfoBox>
                <p>
                  <FaInfoCircle style={{ marginRight: '8px' }} />
                  These are the AI models available to you. You can select any of these models as your default for AI interactions.
                </p>
              </InfoBox>
              
              {error && (
                <div style={{ 
                  backgroundColor: '#fff2f0', 
                  border: '1px solid #ffccc7', 
                  borderRadius: '4px', 
                  padding: '12px 16px', 
                  marginBottom: '16px',
                  color: '#ff4d4f',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaExclamationCircle style={{ marginRight: '8px' }} />
                  {error}
                </div>
              )}
              
              {loadingAssignedModels ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>Loading available models...</div>
              ) : assignedModels.length === 0 ? (
                <div>
                  {/* Always show at least the system default models */}
                  <ModelCard 
                    selected={true}
                  >
                    <ModelName>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FaBrain style={{ marginRight: '8px', color: '#1890ff' }} />
                        {preferences.ai_model || 'gpt-4o'}
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '12px', 
                          backgroundColor: '#52c41a', 
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '10px'
                        }}>
                          Default
                        </span>
                      </div>
                      <ProviderTag provider={preferences.ai_provider || 'openai'}>
                        {(preferences.ai_provider || 'openai').charAt(0).toUpperCase() + (preferences.ai_provider || 'openai').slice(1)}
                      </ProviderTag>
                    </ModelName>
                  </ModelCard>
                  
                  {/* Show available system models */}
                  {availableModels.providers.map(provider => (
                    provider.models.map((model, idx) => (
                      <ModelCard 
                        key={`${provider.name}-${model.id}-${idx}`}
                        selected={preferences.ai_provider === provider.name && preferences.ai_model === model.id}
                      >
                        <ModelName>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FaBrain style={{ marginRight: '8px', color: '#1890ff' }} />
                            {model.name}
                            {preferences.ai_provider === provider.name && preferences.ai_model === model.id && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '12px', 
                                backgroundColor: '#52c41a', 
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '10px'
                              }}>
                                Default
                              </span>
                            )}
                          </div>
                          <ProviderTag provider={provider.name}>
                            {provider.display_name}
                          </ProviderTag>
                        </ModelName>
                        
                        {!(preferences.ai_provider === provider.name && preferences.ai_model === model.id) && (
                          <div style={{ marginTop: '12px', textAlign: 'right' }}>
                            <button
                              onClick={() => handleSetDefaultModel(provider.name, model.id)}
                              disabled={saving || isModelDisabled(provider.name)}
                              style={{
                                backgroundColor: '#1890ff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                opacity: saving || isModelDisabled(provider.name) ? 0.7 : 1
                              }}
                            >
                              {saving ? 'Setting...' : 'Set as Default'}
                            </button>
                          </div>
                        )}
                      </ModelCard>
                    ))
                  ))}
                </div>
              ) : (
                <div>
                  {/* Debug info */}
                  <div style={{ marginBottom: '16px', fontSize: '12px', color: '#666' }}>
                    {assignedModels.length} model(s) assigned
                  </div>
                  
                  {assignedModels.map((model, index) => (
                    <ModelCard 
                      key={`assigned-${model.ai_provider}-${model.ai_model}-${index}`} 
                      selected={model.is_default}
                    >
                      <ModelName>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <FaBrain style={{ marginRight: '8px', color: '#1890ff' }} />
                          {model.ai_model}
                          {model.is_default && (
                            <span style={{ 
                              marginLeft: '8px', 
                              fontSize: '12px', 
                              backgroundColor: '#52c41a', 
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '10px'
                            }}>
                              Default
                            </span>
                          )}
                        </div>
                        <ProviderTag provider={model.ai_provider}>
                          {model.ai_provider.charAt(0).toUpperCase() + model.ai_provider.slice(1)}
                        </ProviderTag>
                      </ModelName>
                      
                      {!model.is_default && (
                        <div style={{ marginTop: '12px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleSetDefaultModel(model.ai_provider, model.ai_model)}
                            // disabled={saving || isModelDisabled(model.ai_provider)}
                            style={{
                              backgroundColor: '#1890ff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              opacity: saving || isModelDisabled(model.ai_provider) ? 0.7 : 1
                            }}
                          >
                            {saving ? 'Setting...' : 'Set as Default'}
                          </button>
                        </div>
                      )}
                    </ModelCard>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AISettingsModal;
